import { useEffect, useRef } from 'react';
import { GRAVITY, getMotionReadings } from 'utils/platform';

// Snatch detector, tuned permissive by design: a missed theft is worse than a false logout
// (product decision). Single-sample trigger, because devicemotion delivery varies ~20–60Hz
// across devices and a snatch peak is narrow (30–80ms) — a multi-sample streak is only
// reliable on fast devices; and budget ±2g accelerometers clip, capping reported linear
// values around 10–16 m/s² no matter how violent the yank. Walking peaks stay ≤ ~6–8.
export const SPIKE_THRESHOLD = 10; // m/s², gravity-free channel
export const SPIKE_STREAK = 1; // consecutive samples; raise only for 60Hz-verified devices
// The |total − g| fallback proxy underestimates jerks not aligned with gravity (~2x when
// horizontal) and clips near 9.8 on ±2g sensors, hence the much lower bar for devices
// without a gravity-free channel.
export const FALLBACK_SPIKE_THRESHOLD = 6; // m/s²
// Freefall (dropped / flying phone): accelerationIncludingGravity reads ≈ 0 while falling.
// Only detectable on that channel — the gravity-free one reads ≈ 0 at rest too. The floor
// keeps sub-250ms tosses (onto a sofa/bed) from triggering.
export const FREEFALL_MAX_MAGNITUDE = 2.5; // m/s²
export const FREEFALL_MIN_DURATION_MS = 250;

export const useDeviceMotion = (
  callback,
  { enabled = true, threshold = SPIKE_THRESHOLD, fallbackThreshold = FALLBACK_SPIKE_THRESHOLD } = {}
) => {
  const callbackRef = useRef(callback);
  callbackRef.current = callback;

  useEffect(() => {
    if (!enabled) return;

    let triggered = false;
    let spikeStreak = 0;
    let freefallSince = null;

    const trigger = (value, reason) => {
      triggered = true;
      window.removeEventListener('devicemotion', handler);
      callbackRef.current(value, reason);
    };

    const handler = (event) => {
      if (triggered) return;

      const { linear, total } = getMotionReadings(event);
      if (linear == null && total == null) return;

      if (total != null && total < FREEFALL_MAX_MAGNITUDE) {
        freefallSince ??= Date.now();
        if (Date.now() - freefallSince >= FREEFALL_MIN_DURATION_MS) {
          trigger(total, 'freefall');
          return;
        }
      } else {
        freefallSince = null;
      }

      // Fallback counts only the high side (total > g): the low side is near-freefall and
      // belongs to the sustained freefall check above — abs() here would instantly fire
      // 'spike' on a single low-total sample, e.g. a soft toss onto a bed.
      const value = linear ?? total - GRAVITY;
      const bar = linear != null ? threshold : fallbackThreshold;
      if (value >= bar) {
        spikeStreak += 1;
        if (spikeStreak >= SPIKE_STREAK) trigger(value, 'spike');
      } else {
        spikeStreak = 0;
      }
    };

    window.addEventListener('devicemotion', handler);
    return () => window.removeEventListener('devicemotion', handler);
  }, [enabled, threshold, fallbackThreshold]);
};
